import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight, Play } from "lucide-react";

// Word-by-word masked reveal
const RevealLine = ({ children, delay = 0, className = "" }) => {
  const words = React.Children.toArray(children)
    .flatMap((c) => (typeof c === "string" ? c.split(" ") : [c]));
  return (
    <span className={`block ${className}`}>
      {words.map((w, i) => (
        <span key={i} className="mask-reveal mr-[0.22em]">
          <motion.span
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{
              duration: 1.1,
              delay: delay + i * 0.07,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            {w}
          </motion.span>
        </span>
      ))}
    </span>
  );
};

const HeroSection = () => {
  const ref = useRef(null);
  const orbRef = useRef(null);
  const [mounted, setMounted] = useState(false);

  // Parallax scroll
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const headlineY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const headlineScale = useTransform(scrollYProgress, [0, 1], [1, 0.9]);
  const headlineOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const orbY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const orbScale = useTransform(scrollYProgress, [0, 1], [1, 0.72]);
  const bloomOpacity = useTransform(scrollYProgress, [0, 0.8], [0.7, 0]);

  // Cursor parallax for orb
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 80, damping: 20 });
  const sy = useSpring(my, { stiffness: 80, damping: 20 });

  useEffect(() => {
    setMounted(true);
    const onMove = (e) => {
      const { innerWidth: w, innerHeight: h } = window;
      mx.set((e.clientX - w / 2) / w);
      my.set((e.clientY - h / 2) / h);
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [mx, my]);

  const orbTransX = useTransform(sx, (v) => v * 40);
  const orbTransY = useTransform(sy, (v) => v * 40);
  const orbRotX = useTransform(sy, (v) => v * -10);
  const orbRotY = useTransform(sx, (v) => v * 10);

  const scrollToNext = () => {
    const el = document.querySelector("#logos");
    if (el && window.__lenis) window.__lenis.scrollTo(el, { duration: 1.4 });
  };

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-container"
      className="relative min-h-[100svh] w-full overflow-hidden"
    >
      {/* Bloom glow */}
      <motion.div
        style={{ opacity: bloomOpacity }}
        className="absolute inset-0 pointer-events-none"
      >
        <div
          className="bloom"
          style={{
            width: "70vw",
            height: "70vw",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -40%)",
            opacity: 0.55,
          }}
        />
        <div
          className="bloom"
          style={{
            width: "40vw",
            height: "40vw",
            top: "20%",
            right: "-10%",
            opacity: 0.3,
            background:
              "radial-gradient(circle, rgba(255,195,100,0.6), transparent 70%)",
          }}
        />
      </motion.div>

      {/* Hairline grid */}
      <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />

      {/* Floating glass orb centerpiece */}
      <motion.div
        ref={orbRef}
        style={{
          x: orbTransX,
          y: orbY,
          scale: orbScale,
          rotateX: orbRotX,
          rotateY: orbRotY,
        }}
        className="absolute top-[52%] left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
        data-testid="hero-orb"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.6, filter: "blur(40px)" }}
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
          transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
          style={{ y: orbTransY }}
          className="hero-orb"
        >
          <div className="hero-orb-ring r3" />
          <div className="hero-orb-ring r2" />
          <div className="hero-orb-ring" />
          <div className="hero-orb-inner" />
          <div className="hero-orb-highlight" />
          {/* Floating particles */}
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 rounded-full bg-accent"
              style={{
                top: `${20 + i * 11}%`,
                left: `${15 + (i % 3) * 30}%`,
                boxShadow: "0 0 10px rgba(255,158,0,0.8)",
              }}
              animate={{
                y: [0, -12, 0],
                opacity: [0.3, 1, 0.3],
              }}
              transition={{
                duration: 3 + i * 0.4,
                repeat: Infinity,
                delay: i * 0.3,
                ease: "easeInOut",
              }}
            />
          ))}
        </motion.div>
      </motion.div>

      {/* Headline + copy */}
      <motion.div
        style={{ y: headlineY, scale: headlineScale, opacity: headlineOpacity }}
        className="relative z-10 flex flex-col items-start justify-center min-h-[100svh] max-w-[1280px] mx-auto px-6 sm:px-10 pt-32 pb-16"
      >
        {/* Status tag */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mono-label flex items-center gap-2 mb-8"
        >
          <span className="h-px w-8 bg-accent" />
          Z CRAZY · STATIC WEB STUDIO · EST. 2021
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="hero-headline font-display font-extrabold tracking-tighter leading-[0.88] text-[clamp(3.2rem,9vw,10rem)] uppercase text-[#F4F4F2] max-w-[18ch]"
        >
          <RevealLine delay={0.3}>Sites that</RevealLine>
          <RevealLine delay={0.5}>
            <span className="text-[#F4F4F2]">load like</span>{" "}
            <span className="font-editorial font-light italic text-accent">light,</span>
          </RevealLine>
          <RevealLine delay={0.75}>
            <span className="font-light text-[#8A8D98]">feel like</span> cinema.
          </RevealLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 max-w-[48ch] text-[17px] sm:text-[19px] leading-relaxed text-[#8A8D98]"
        >
          Z Crazy is a two-person studio engineering hand-built, zero-JS static
          sites for founders, galleries and product teams who are allergic to
          templates. Landing pages, portfolios, launch pages — delivered in 14
          days flat.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.4, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-primary-cta"
            onClick={() => window.__lenis?.scrollTo("#final-cta", { duration: 1.4 })}
            className="btn-primary"
          >
            Book a studio sprint
            <ArrowUpRight size={16} />
          </button>
          <button
            data-testid="hero-secondary-cta"
            onClick={() => window.__lenis?.scrollTo("#showcase", { duration: 1.4 })}
            className="btn-ghost"
          >
            <Play size={14} className="text-accent" />
            See the reel
          </button>
        </motion.div>

        {/* Meta row */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1.6 }}
          className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-6 w-full max-w-[720px] pt-8 border-t border-white/5"
        >
          {[
            ["Delivery", "14 days"],
            ["Approach", "Hand-built"],
            ["JS payload", "< 24 kb"],
            ["Lighthouse", "100 / 100"],
          ].map(([k, v]) => (
            <div key={k} className="flex flex-col gap-1">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#5A5D68]">
                {k}
              </span>
              <span className="font-display text-lg font-medium text-[#F4F4F2]">
                {v}
              </span>
            </div>
          ))}
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.button
        onClick={scrollToNext}
        initial={{ opacity: 0 }}
        animate={{ opacity: mounted ? 1 : 0 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 group"
        data-testid="hero-scroll-cue"
        aria-label="Scroll down"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5A5D68] group-hover:text-accent transition-colors">
          Scroll
        </span>
        <div className="relative h-10 w-[1px] bg-white/10 overflow-hidden">
          <motion.div
            className="absolute inset-x-0 h-4 bg-accent"
            animate={{ y: [-16, 40] }}
            transition={{
              duration: 1.8,
              repeat: Infinity,
              ease: [0.6, 0.01, 0.4, 1],
            }}
          />
        </div>
      </motion.button>
    </section>
  );
};

export default HeroSection;
